import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, LayoutDashboard, AlertTriangle, Lightbulb,
  Shield, Users, Clock, Tag, BarChart3, PiggyBank, Calculator,
  FileBarChart, Settings, Sparkles, ArrowRight, Cloud, Database,
  TrendingUp, Zap, Mail, GitBranch, FileText, Receipt, Wand2,
  Activity, Bot, KeyRound, Plug,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommandPalette = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  const allCommands = [
    // Visibility — most-used dashboards
    { id: 'nav-dashboard', label: 'Dashboard', icon: LayoutDashboard, action: () => navigate('/dashboard'), category: 'Visibility' },
    { id: 'nav-tech-spend', label: 'Total Tech Spend', icon: TrendingUp, action: () => navigate('/tech-spend'), category: 'Visibility' },
    { id: 'nav-customer-pl', label: 'Customer P&L (Stripe)', icon: Receipt, action: () => navigate('/customer-pl'), category: 'Visibility' },
    { id: 'nav-ai-pl', label: 'AI Feature P&L', icon: Sparkles, action: () => navigate('/ai-feature-profitability'), category: 'Visibility' },
    { id: 'nav-pr-cost', label: 'PR-Time Cost Predictor', icon: GitBranch, action: () => navigate('/pr-cost-predictor'), category: 'Visibility' },
    // v35.19 — /customer-economics consolidated into /customer-pl + Bill Detective Per-Customer tab
    { id: 'nav-cost-dna', label: 'Cost DNA', icon: BarChart3, action: () => navigate('/cost-dna'), category: 'Visibility' },
    { id: 'nav-cost-graph', label: 'Cost Graph', icon: BarChart3, action: () => navigate('/cost-graph'), category: 'Visibility' },
    { id: 'nav-health', label: 'Cost Health Score', icon: Activity, action: () => navigate('/health-score'), category: 'Visibility' },
    { id: 'nav-industry', label: 'Industry Intel', icon: BarChart3, action: () => navigate('/industry'), category: 'Visibility' },
    { id: 'nav-comparison', label: 'Compare Periods', icon: BarChart3, action: () => navigate('/comparison'), category: 'Visibility' },
    { id: 'nav-weather', label: 'Cost Weather (Forecast)', icon: Activity, action: () => navigate('/weather'), category: 'Visibility' },
    { id: 'nav-search', label: 'AI Search', icon: Search, action: () => navigate('/search'), category: 'Visibility' },
    { id: 'nav-cmd-center', label: 'AI Command Center', icon: Bot, action: () => navigate('/ai-command-center'), category: 'Visibility' },
    { id: 'nav-reports', label: 'Reports', icon: FileBarChart, action: () => navigate('/reports'), category: 'Visibility' },
    { id: 'nav-export', label: 'Export Center', icon: FileText, action: () => navigate('/export'), category: 'Visibility' },

    // LLM Engineering Suite
    { id: 'nav-prompt-playground', label: 'Prompt Playground (multi-model)', icon: Sparkles, action: () => navigate('/prompt-playground'), category: 'LLM Eng' },
    { id: 'nav-prompts', label: 'Prompt Library', icon: FileText, action: () => navigate('/prompts'), category: 'LLM Eng' },
    { id: 'nav-traces', label: 'LLM Traces (waterfall)', icon: Activity, action: () => navigate('/traces'), category: 'LLM Eng' },
    { id: 'nav-evals', label: 'Evals + A/B Compare', icon: BarChart3, action: () => navigate('/evals'), category: 'LLM Eng' },
    { id: 'nav-llm-proxy', label: 'LLM Proxy (drop-in observability)', icon: Activity, action: () => navigate('/llm-proxy'), category: 'LLM Eng' },
    { id: 'nav-saas-spend', label: 'SaaS Spend Portfolio (13 vendor templates)', icon: Database, action: () => navigate('/saas-spend'), category: 'FinOps' },

    // Smart Engines — 20 proprietary sub-systems (the moat)
    { id: 'nav-cartie-lens', label: 'CARTIE Lens — Cost-Linter for app code', icon: Search, action: () => navigate('/cartie-lens'), category: 'Smart Engines' },
    { id: 'nav-cost-bisect', label: 'Cost-Bisect — git-bisect for cost', icon: GitBranch, action: () => navigate('/cost-bisect'), category: 'Smart Engines' },
    { id: 'nav-batch-router', label: 'Batch Router — Sync↔Batch LLM routing', icon: ArrowRight, action: () => navigate('/batch-router'), category: 'Smart Engines' },
    { id: 'nav-qa-failover', label: 'QA Failover — Quality-adaptive LLM failover', icon: Shield, action: () => navigate('/qa-failover'), category: 'Smart Engines' },
    { id: 'nav-reverse-tag', label: 'Reverse-Tag Inferencer — AI-inferred cost centers', icon: Tag, action: () => navigate('/reverse-tag'), category: 'Smart Engines' },
    { id: 'nav-k8s-shapley', label: 'K8s Shapley Attribution — fair pod cost split', icon: Database, action: () => navigate('/k8s-cost'), category: 'Smart Engines' },
    { id: 'nav-cost-causality', label: 'Cost Causality — growth vs waste decomposition', icon: TrendingUp, action: () => navigate('/cost-causality'), category: 'Smart Engines' },
    { id: 'nav-safe-autonomy', label: 'Safe Autonomy — blast-radius + auto-rollback', icon: Shield, action: () => navigate('/safe-autonomy'), category: 'Smart Engines' },
    { id: 'nav-carbon-pareto', label: 'Carbon-Cost Pareto — joint $/CO2 optimization', icon: Activity, action: () => navigate('/carbon-cost'), category: 'Smart Engines' },
    { id: 'nav-forward-pred', label: 'Forward Cost Predictor — 4-signal fusion forecasting', icon: TrendingUp, action: () => navigate('/forward-predict'), category: 'Smart Engines' },
    { id: 'nav-rt-unit-econ', label: 'Real-Time Unit Economics — per-trace cost', icon: Activity, action: () => navigate('/unit-econ'), category: 'Smart Engines' },
    { id: 'nav-copilot-finops', label: 'FinOps Copilot — cross-team orchestration', icon: Bot, action: () => navigate('/copilot'), category: 'Smart Engines' },
    { id: 'nav-shared-cost', label: 'Shared Resource Allocator — Shapley for RDS/S3/Kafka', icon: Database, action: () => navigate('/shared-cost'), category: 'Smart Engines' },
    { id: 'nav-tag-canon', label: 'Tag Canonicaliser — cross-cloud tag namespace', icon: Tag, action: () => navigate('/tag-canon'), category: 'Smart Engines' },
    { id: 'nav-gpu-spot', label: 'GPU Spot Bidder — checkpoint-aware optimization', icon: Zap, action: () => navigate('/gpu-spot'), category: 'Smart Engines' },
    { id: 'nav-scope3', label: 'Scope-3 Carbon — value-chain CO2 attribution', icon: Activity, action: () => navigate('/scope3'), category: 'Smart Engines' },
    { id: 'nav-causal-bot', label: 'Causal Bot — natural-language "why did the bill spike?"', icon: Bot, action: () => navigate('/causal-bot'), category: 'Smart Engines' },
    { id: 'nav-multi-currency', label: 'Multi-Currency P&L — FX exposure isolation', icon: Receipt, action: () => navigate('/multi-currency'), category: 'Smart Engines' },
    { id: 'nav-finops-integrations', label: 'FinOps Data Provider Integrations — switch engines to LIVE', icon: Plug, action: () => navigate('/integrations/data-providers'), category: 'Manage' },

    // Snowflake
    { id: 'nav-snowflake', label: 'Snowflake Dashboard', icon: Database, action: () => navigate('/snowflake'), category: 'Snowflake' },
    { id: 'nav-snow-queries', label: 'Snowflake Query Explorer', icon: Database, action: () => navigate('/snowflake-queries'), category: 'Snowflake' },
    { id: 'nav-snow-alerts', label: 'Snowflake Credit Alerts', icon: AlertTriangle, action: () => navigate('/snowflake-alerts'), category: 'Snowflake' },
    { id: 'nav-spike', label: 'Spike Autopsy', icon: AlertTriangle, action: () => navigate('/spike-autopsy'), category: 'Snowflake' },
    { id: 'nav-query-est', label: 'Query Cost Estimator', icon: Calculator, action: () => navigate('/query-estimator'), category: 'Snowflake' },
    { id: 'nav-storage-det', label: 'Storage Detective', icon: Database, action: () => navigate('/storage-detective'), category: 'Snowflake' },
    { id: 'nav-team-chargeback', label: 'Team Chargeback', icon: Users, action: () => navigate('/team-chargeback'), category: 'Snowflake' },
    { id: 'nav-budget-countdown', label: 'Budget Countdown', icon: Clock, action: () => navigate('/budget-countdown'), category: 'Snowflake' },

    // Databricks
    { id: 'nav-databricks', label: 'Databricks Dashboard', icon: Database, action: () => navigate('/databricks'), category: 'Databricks' },

    // Optimize
    { id: 'nav-invoice', label: 'Invoice Autopsy', icon: Receipt, action: () => navigate('/invoice-autopsy'), category: 'Optimize' },
    { id: 'nav-zombie', label: 'Zombie Killer', icon: AlertTriangle, action: () => navigate('/zombie-killer'), category: 'Optimize' },
    { id: 'nav-pilot', label: 'Auto Pilot', icon: Zap, action: () => navigate('/pilot'), category: 'Optimize' },
    { id: 'nav-bill-detective', label: 'Bill Detective', icon: Search, action: () => navigate('/bill-detective'), category: 'Optimize' },
    // v35.19 — /cost-scorecard consolidated into Bill Detective Engineer Leaderboard tab
    { id: 'nav-saas-detective', label: 'SaaS Detective', icon: Search, action: () => navigate('/saas-detective'), category: 'Optimize' },
    { id: 'nav-smart-tags', label: 'Smart Tag Fixer', icon: Tag, action: () => navigate('/smart-tags'), category: 'Optimize' },
    { id: 'nav-egress', label: 'Egress Shield', icon: Shield, action: () => navigate('/egress-shield'), category: 'Optimize' },
    { id: 'nav-board-deck', label: 'Board Deck Generator', icon: FileText, action: () => navigate('/board-deck'), category: 'Optimize' },
    { id: 'nav-vendor', label: 'Vendor Deal Intel', icon: Receipt, action: () => navigate('/vendor-deals'), category: 'Optimize' },
    { id: 'nav-compliance-map', label: 'Compliance Mapper', icon: Shield, action: () => navigate('/compliance-mapper'), category: 'Optimize' },
    { id: 'nav-carbon', label: 'Carbon + ESG', icon: Activity, action: () => navigate('/carbon'), category: 'Optimize' },
    { id: 'nav-unit-econ', label: 'Unit Economics', icon: Calculator, action: () => navigate('/unit-economics'), category: 'Optimize' },
    { id: 'nav-ai-gpu', label: 'AI/GPU Intelligence', icon: Sparkles, action: () => navigate('/ai-gpu'), category: 'Optimize' },
    { id: 'nav-commit-intel', label: 'Commitment Intel', icon: PiggyBank, action: () => navigate('/commitment-intel'), category: 'Optimize' },
    { id: 'nav-recs', label: 'Recommendations', icon: Lightbulb, action: () => navigate('/recommendations'), category: 'Optimize' },
    { id: 'nav-explainer', label: 'AI Cost Explainer', icon: Bot, action: () => navigate('/cost-explainer'), category: 'Optimize' },
    { id: 'nav-voice', label: 'Voice FinOps Assistant', icon: Bot, action: () => navigate('/voice-assistant'), category: 'Optimize' },
    { id: 'nav-terraform', label: 'Terraform Preview', icon: GitBranch, action: () => navigate('/terraform-preview'), category: 'Optimize' },
    { id: 'nav-calculator', label: 'What-If Calculator', icon: Calculator, action: () => navigate('/calculator'), category: 'Optimize' },
    { id: 'nav-peer', label: 'Peer Benchmark', icon: BarChart3, action: () => navigate('/peer-benchmark'), category: 'Optimize' },
    { id: 'nav-1click', label: 'One-Click Fix', icon: Zap, action: () => navigate('/one-click-fix'), category: 'Optimize' },
    { id: 'nav-scheduler', label: 'Resource Scheduler', icon: Clock, action: () => navigate('/scheduler'), category: 'Optimize' },
    { id: 'nav-time-machine', label: 'Time Machine', icon: Clock, action: () => navigate('/time-machine'), category: 'Optimize' },
    { id: 'nav-savings', label: 'Savings Tracker', icon: PiggyBank, action: () => navigate('/savings'), category: 'Optimize' },
    { id: 'nav-freeze', label: 'Spend Freeze', icon: Shield, action: () => navigate('/spend-freeze'), category: 'Optimize' },
    { id: 'nav-conscience', label: 'Cost Conscience', icon: Bot, action: () => navigate('/cost-conscience'), category: 'Optimize' },
    { id: 'nav-ai-cfo', label: 'AI CFO', icon: Sparkles, action: () => navigate('/ai-cfo'), category: 'Optimize' },

    // Manage
    { id: 'nav-cloud-audit', label: 'Free Cloud Audit', icon: Cloud, action: () => navigate('/cloud-audit'), category: 'Manage' },
    { id: 'nav-integrations', label: 'Cloud Integrations', icon: Cloud, action: () => navigate('/cloud-integrations'), category: 'Manage' },
    { id: 'nav-builder', label: 'Dashboard Builder', icon: LayoutDashboard, action: () => navigate('/dashboard-builder'), category: 'Manage' },
    { id: 'nav-teams', label: 'Teams', icon: Users, action: () => navigate('/teams'), category: 'Manage' },
    { id: 'nav-alloc', label: 'Cost Allocation Engine', icon: Calculator, action: () => navigate('/allocation'), category: 'Manage' },
    { id: 'nav-collab', label: 'Collaboration', icon: Users, action: () => navigate('/collaboration'), category: 'Manage' },
    { id: 'nav-tagging', label: 'Tagging Compliance', icon: Tag, action: () => navigate('/tagging'), category: 'Manage' },
    { id: 'nav-k8s', label: 'Kubernetes', icon: Cloud, action: () => navigate('/kubernetes'), category: 'Manage' },
    // v35.19 — Git-to-Bill + PR Cost Impact consolidated into Bill Detective Code→Cost tab
    { id: 'nav-guardrails', label: 'Budget Guardrails', icon: Shield, action: () => navigate('/guardrails'), category: 'Manage' },
    { id: 'nav-bill-shock', label: 'Bill Shock Prevention', icon: AlertTriangle, action: () => navigate('/bill-shock'), category: 'Manage' },
    { id: 'nav-notifs', label: 'Notification Center', icon: Mail, action: () => navigate('/notifications'), category: 'Manage' },
    { id: 'nav-cost-bot', label: 'Slack/Teams Bot', icon: Bot, action: () => navigate('/cost-bot'), category: 'Manage' },

    // Trust & Help — public marketing surfaces (Feb 24 2026)
    { id: 'nav-docs', label: 'Developer Docs', icon: FileText, action: () => navigate('/docs'), category: 'Help' },
    { id: 'nav-status', label: 'System Status', icon: Activity, action: () => navigate('/status'), category: 'Help' },
    { id: 'nav-case-aurelia', label: 'Pilot Case Study · Aurelia AI', icon: TrendingUp, action: () => navigate('/case-study/aurelia-ai'), category: 'Help' },
    { id: 'nav-honest', label: 'Honest Scorecard · vs Vantage/CloudHealth', icon: BarChart3, action: () => navigate('/honest-scorecard'), category: 'Help' },
    { id: 'nav-changelog', label: 'Changelog', icon: FileText, action: () => navigate('/changelog'), category: 'Help' },
    { id: 'nav-policy-pg', label: 'Policy Playground · Rego editor', icon: Wand2, action: () => navigate('/policy-playground'), category: 'Help' },
    { id: 'nav-faq', label: 'FAQ', icon: FileText, action: () => navigate('/faq'), category: 'Help' },
    { id: 'nav-pricing-pub', label: 'Pricing', icon: PiggyBank, action: () => navigate('/pricing'), category: 'Help' },
    { id: 'nav-soc2-pub', label: 'SOC 2 Readiness', icon: Shield, action: () => navigate('/soc2'), category: 'Help' },
    { id: 'nav-budget-neg', label: 'Budget Negotiator · PR-time gating', icon: GitBranch, action: () => navigate('/budget-negotiator'), category: 'Help' },
    { id: 'nav-soc2', label: 'SOC 2 / Compliance', icon: Shield, action: () => navigate('/compliance'), category: 'Manage' },
    { id: 'nav-sec-center', label: 'Security Center', icon: Shield, action: () => navigate('/security-center'), category: 'Manage' },
    { id: 'nav-token-intel', label: 'Token Intelligence', icon: Wand2, action: () => navigate('/token-intelligence'), category: 'Manage' },

    // Admin & Settings
    { id: 'nav-analytics', label: 'Analytics Dashboard', icon: BarChart3, action: () => navigate('/analytics'), category: 'Admin' },
    { id: 'nav-digest', label: 'Weekly Digest', icon: Mail, action: () => navigate('/weekly-digest'), category: 'Admin' },
    { id: 'nav-ab', label: 'A/B Testing', icon: Sparkles, action: () => navigate('/ab-testing'), category: 'Admin' },
    { id: 'nav-sso', label: 'Enterprise SSO', icon: KeyRound, action: () => navigate('/enterprise-sso'), category: 'Admin' },
    { id: 'nav-preflight', label: 'Launch Pre-flight Check', icon: Activity, action: () => navigate('/admin/pre-flight'), category: 'Admin' },
    { id: 'nav-funnel', label: 'Conversion Funnel', icon: BarChart3, action: () => navigate('/admin/conversion-funnel'), category: 'Admin' },
    { id: 'nav-settings', label: 'Settings', icon: Settings, action: () => navigate('/settings'), category: 'Admin' },
    { id: 'nav-billing', label: 'Billing & Plan', icon: Receipt, action: () => navigate('/settings/billing'), category: 'Admin' },
    { id: 'nav-api-keys', label: 'API Keys', icon: KeyRound, action: () => navigate('/settings/api-keys'), category: 'Admin' },
    { id: 'nav-slack-cfg', label: 'Slack Integration', icon: Bot, action: () => navigate('/settings/slack'), category: 'Admin' },

    // Quick Actions
    { id: 'action-detect', label: 'Run Anomaly Detection', icon: AlertTriangle, action: async () => {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/anomalies/detect`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }});
      navigate('/anomalies');
    }, category: 'Quick Actions' },
    { id: 'action-generate', label: 'Generate Recommendations', icon: Lightbulb, action: async () => {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/recommendations/generate`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }});
      navigate('/recommendations');
    }, category: 'Quick Actions' },
    { id: 'action-export-csv', label: 'Export Costs to CSV', icon: FileBarChart, action: () => {
      window.open(`${API_URL}/api/reports/costs/csv`, '_blank');
    }, category: 'Quick Actions' },
    { id: 'action-export-pdf', label: 'Generate PDF Report', icon: FileBarChart, action: () => {
      window.open(`${API_URL}/api/reports/pdf`, '_blank');
    }, category: 'Quick Actions' },
  ];

  const filteredCommands = query
    ? allCommands.filter(cmd => 
        cmd.label.toLowerCase().includes(query.toLowerCase()) ||
        cmd.category.toLowerCase().includes(query.toLowerCase())
      )
    : allCommands;

  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  const flatCommands = filteredCommands;

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatCommands[selectedIndex]) {
      e.preventDefault();
      flatCommands[selectedIndex].action();
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [flatCommands, selectedIndex, onClose]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div 
        className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search commands, pages, actions..."
            className="flex-1 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 outline-none text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 rounded">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {Object.entries(groupedCommands).map(([category, commands]) => (
            <div key={category} className="mb-2">
              <div className="px-3 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wider">
                {category}
              </div>
              {commands.map((cmd, idx) => {
                const globalIdx = flatCommands.findIndex(c => c.id === cmd.id);
                const isSelected = globalIdx === selectedIndex;
                return (
                  <button
                    key={cmd.id}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      isSelected 
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' 
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                    onClick={() => { cmd.action(); onClose(); }}
                    onMouseEnter={() => setSelectedIndex(globalIdx)}
                  >
                    <cmd.icon className={`w-4 h-4 ${isSelected ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span className="flex-1 text-sm font-medium">{cmd.label}</span>
                    {isSelected && <ArrowRight className="w-4 h-4 text-emerald-500" />}
                  </button>
                );
              })}
            </div>
          ))}
          
          {filteredCommands.length === 0 && (
            <div className="py-8 text-center text-slate-400">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No results found for "{query}"</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">↵</kbd>
              Select
            </span>
          </div>
          <span className="text-xs text-slate-400">
            {filteredCommands.length} commands
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
